
<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import { required, minLength, maxLength, maxValue } from 'vuelidate/lib/validators'
import CourseGeneral from './CourseGeneral'
import CourseDocuments from './CourseDocuments'
import CourseManagers from './CourseManagers'
import CourseAffiliates from './CourseAffiliates'
import CourseCertificates from './CourseCertificates'
import CourseClasses from './CourseClasses'
import CourseNotes from './CourseNotes'
import DocumentUpload from '../Document/DocumentUpload'

export default {
  name: 'Details',
  components: {
    CourseGeneral,
    CourseDocuments,
    CourseManagers,
    CourseAffiliates,
    CourseCertificates,
    CourseNotes,
    CourseClasses,
    DocumentUpload
  },
  data () {
    return {
      title: this.$route.meta.title,
      module: this.$route.meta.module,
      sparse: true,
      separator: true,
      managerFilter: '',
      affiliateFilter: '',
      addManager: false,
      addAffiliate: false,
      maxResults: 10,
      removeDialogOptions: {
        title: 'Remove Course?',
        message: 'Are you sure you want to remove this course?',
        ok: {
          label: 'Remove',
          color: 'negative'
        },
        cancel: {
          label: 'Cancel'
        }
      }
    }
  },
  validations: {
    courseForm: {
      COURSE_NAME: { required, minLength: minLength(4), maxLength: maxLength(65) },
      COURSE_LENGTH_HOURS: { maxValue: maxValue(23) },
      COURSE_LENGTH_MINUTES: { maxValue: maxValue(59) },
      COURSE_LENGTH_SECONDS: { maxValue: maxValue(59) }
    }
  },
  computed: {
    validation () { return this.$v },
    ...mapState({
      mode: state => state.shared.detailsModal.mode
    }),
    ...mapState('courses', {
      courseForm: state => state.courseForm,
      selectedTab: state => state.selectedTab,
      selectedManagers: state => state.selectedManagers,
      selectedAffiliates: state => state.selectedAffiliates
    }),
    ...mapGetters('courses', [
      'courseList',
      'courseAffiliateOptions',
      'courseManagerOptions'
    ])
  },
  methods: {
    test (tab) {
    },
    ...mapActions('courses', [
      'add',
      'addNew',
      'select',
      'update',
      'remove',
      'fetchAffiliates',
      'fetchManagers',
      'createCourseManager',
      'createCourseAffiliate',
      'removeCourseManagers',
      'removeCourseAffiliates'
    ]),
    ...mapMutations('courses', [
      'updateSelectedTab',
      'clearSelectedManagers',
      'clearSelectedAffiliates'
    ]),
    ...mapMutations([
      'closeDetails'
    ]),
    successHandler (response) {
      this.$v.$reset()
    },
    onSelect (course) {
      this.select(course).then(this.successHandler)
    },
    onRemove () {
      this.$q.dialog(this.removeDialogOptions)
      .then(this.remove).catch(err => err)
    },
    onAddNew () {
      this.addNew().then(this.successHandler)
    },
    onAdd () {
      this.add().then(this.successHandler)
    },
    onAddManager () {
      this.managerFilter = ''
      this.addManager = true
    },
    onAddAffiliate () {
      this.affiliateFilter = ''
      this.addAffiliate = true
    },
    onClearManager () {
      this.managerFilter = ''
      this.addManager = false
    },
    onClearAffiliate () {
      this.affiliateFilter = ''
      this.addAffiliate = false
    },
    onSearchManager (s, done) {
      this.$store.dispatch('users/fetchManagerAutoComplete', { s })
      setTimeout(() => {
        done(this.courseManagerOptions)
      }, 1000)
    },
    onSearchAffiliate (s, done) {
      this.$store.dispatch('users/fetchAffiliateAutoComplete', { s })
      setTimeout(() => {
        done(this.courseAffiliateOptions)
      }, 1000)
    },
    onSelectManager (manager) {
      this.createCourseManager({ MANAGER_ID: manager.item.MANAGER_ID })
      .then(this.onClearManager())
    },
    onSelectAffiliate (affiliate) {
      this.createCourseAffiliate({ AFFILIATE_ID: affiliate.item.AFFILIATE_ID })
      .then(this.fetchAffiliates())
      .then(this.onClearAffiliate())
    },
    onRemoveManagers () {
      const vm = this
      this.removeCourseManagers()
      .then(this.fetchAffiliates())
      .then(this.clearSelectedManagers())
    },
    onRemoveAffiliates () {
      const vm = this
      this.removeCourseAffiliates()
      .then(() => vm.clearSelectedAffiliates())
    },
    openUploadModal () {
      this.$store.dispatch('documents/openUploadModal', 
      { LK_ENTITY_ID: 3, ENTITY_ITEM_ID: this.courseForm.COURSE_ID }, 
      { root: true })
    }
  }
}

</script>

<template>      
  <div class="details-inner">
    <q-toolbar slot="footer" class="sl-footer">
        <q-btn v-if="(selectedTab === 'managers') && (selectedManagers.length !== 0)" label="Remove Selected" color="red" @click="onRemoveManagers()"/>
        <q-btn v-if="(selectedTab === 'affiliates') && (selectedAffiliates.length !== 0)" label="Remove Selected" color="red" @click="onRemoveAffiliates()"/>
        <q-toolbar-title>
        </q-toolbar-title>
        <div class="q-px-sm" v-if="mode === 'edit' && selectedTab === 'documents'">
          <q-btn            
            class="q-px-md"
            label="Upload"
            color="green"
            @click="openUploadModal()"
          />
        </div>
        <div class="q-px-sm" v-if="mode === 'edit'">
          <q-btn            
            class="q-px-md"
            color="green"
            label="Save"
            :disable="$v.courseForm.$invalid"
            @click.native="update()"
          />
        </div>
        <div class="q-px-sm" v-if="mode === 'add'">
          <q-btn            
            class="q-px-md"
            color="green"
            label="Add"
            :disable="$v.courseForm.$invalid"
            @click.native="onAdd()"
          />
        </div>
        <q-btn outline color="faded" @click.native="closeDetails" label="Close" />
      </q-toolbar>
    <div id="orderManagementBody" class="layout-padding">
      <div class="sl-row sl-abs">
        <div class="sl-col-3 navigation sl-vp">
          <q-list highlight>
            <q-item :class="{active:selectedTab === 'general'}" @click.native="updateSelectedTab('general'); test()">
                <q-item-main label="General" />
            </q-item>
            <q-item :class="{active:selectedTab === 'managers'}" @click.native="updateSelectedTab('managers')" v-if="mode != 'add' && $p(['ADMIN'])">
              <q-item-main v-show="!addManager" label="Managers" />
              <q-item-main v-show="addManager">
                <q-field error-label="Invalid Date">
                  <q-search v-model="managerFilter" >
                    <q-autocomplete 
                      ref="managerAutoComplete"
                      stack-label="Search Managers"
                      :debounce="500"
                      :max-results="maxResults"
                      @search="onSearchManager"
                      @selected="onSelectManager"
                    />
                  </q-search>
                </q-field>
              </q-item-main>
              <q-item-side>
                <q-item-tile>
                  <q-btn v-show="!addManager" class="addItemBtn" icon="add" @click="onAddManager()">
                    <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Add a Manager</q-tooltip>
                  </q-btn>
                  <q-btn v-show="addManager" class="clearItemBtn" flat color="red" icon="clear" @click="onClearManager()">
                    <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Clear</q-tooltip>
                  </q-btn>
                </q-item-tile>
              </q-item-side>
            </q-item>
            <q-item :class="{active:selectedTab === 'affiliates'}" @click.native="updateSelectedTab('affiliates')" v-if="mode != 'add'">
              <q-item-main v-show="!addAffiliate" label="Affiliate" />
              <q-item-main v-show="addAffiliate">
                <q-field error-label="Invalid Date">
                  <q-search v-model="affiliateFilter" stack-label="Search Affiliate" >
                    <q-autocomplete 
                      ref="affiliateAutoComplete"
                      stack-label="Search Affiliate"
                      :debounce="500"
                      :max-results="maxResults"
                      @search="onSearchAffiliate"
                      @selected="onSelectAffiliate"
                    />
                  </q-search>
                </q-field>
              </q-item-main>
              <q-item-side>
                <q-item-tile>
                  <q-btn v-show="!addAffiliate" class="addItemBtn" icon="add" @click="onAddAffiliate()">
                    <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Add an Affiliate</q-tooltip>
                  </q-btn>
                  <q-btn v-show="addAffiliate" class="clearItemBtn" flat color="red" icon="clear" @click="onClearAffiliate()">
                    <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Clear</q-tooltip>
                  </q-btn>
                </q-item-tile>
              </q-item-side>
            </q-item>
            <q-item v-if="mode === 'edit'" :class="{active:selectedTab === 'documents'}" @click.native="updateSelectedTab('documents')">
                <q-item-main label="Documents" />
            </q-item>
            <q-item v-if=" mode === 'edit'" :class="{active:selectedTab === 'classes'}" @click.native="updateSelectedTab('classes')">
                <q-item-main label="Classes" />
            </q-item>
            <q-item v-if="mode === 'edit'" :class="{active:selectedTab === 'notes'}" @click.native="updateSelectedTab('notes')">
                <q-item-main label="Notes" />
            </q-item>
          </q-list>
        </div>
        <div id="class-modal-right" class="sl-col-9 sl-vp" style="padding-bottom:60px">
          <!-- General -->
          <div v-if="selectedTab === 'general'">
            <course-general :courseForm="courseForm" :validation="validation"/>
          </div>
          <!-- Classes -->
          <div v-if="selectedTab === 'classes'">
            <course-classes />
          </div>
          <!-- Managers -->
          <div v-if="selectedTab === 'managers'">
            <course-managers />
          </div>
          <!-- Affiliates -->
          <div v-if="selectedTab === 'affiliates'">
            <course-affiliates />
          </div>
          <!-- Documents -->
          <div v-if="selectedTab === 'documents'">
            <course-documents />
          </div>
          <!-- Notes -->
          <div v-if="selectedTab === 'notes'">
            <course-notes />
          </div>
        </div>    
      </div>
    </div>
  </div>
</template>

<style scoped lang="stylus">
  @import '~variables'
.q-layout-header
  .q-btn-dropdown
      padding 0 7px
      .q-icon
          color toolbar-icon-color
          &.on-left
              margin-right 0
              font-size 27px
      .q-btn-dropdown-arrow
          display none
.clearOverrideBtn {
 width: 5px
}
.submitOverrideBtn {
  width: 10px
}
.addOverrideBtn {
  width: 10px
}
.sl-modal
  &.maximized
      .modal-content
          width 100% !important
      .q-input,
      .q-datetime-input,
      h5
          transition(all .5s ease-in-out)
          opacity 1
  .approval-history
      font-size 19px
  #class-modal-right
      position relative
  #sessionDropdown
      .ellipsis
          overflow visible
  .total-students
      color text-color
      display inline-block
      padding-left 0
      padding-bottom 13px
      padding-top 20px
  .q-inner-loading
      z-index 9
  .loading
      .q-input,
      .q-datetime-input
          margin-bottom 100px
          opacity 0
      h5
          margin-top 40px
          margin-bottom 60px
          opacity 0
  h5
      margin-top 30px
      input[type="checkbox"]
          margin-left 20px
      span 
          font-size 13px
          vertical-align middle
    .navigation   
      border-right 1px solid border-color
      position relative
      .q-list
          border 0
          margin-top 20px
          margin-left -20px
          margin-right -24px
          .label
              transition(all 0.4s ease-in-out)
              &.round
                  float right
                  background #dedede
                  color #000
      .q-item-label
          font-weight 400
          font-size 18px
      .q-item 
          border-right-width 5px
          border-right-style solid
          border-right-color transparent
          padding 10px 30px
          &:hover:not(.active)
              cursor pointer
          &.active
              background transparent 
              color $primary
              border-right-color $primary
              .label
                  background $primary
                  color white
  #addOrderType
    margin-left 10px
    margin-right -16px
    padding 0 7px
    .q-icon
      color toolbar-icon-color
      &.on-left
        margin-right 0
        font-size 27px
  #submitOrderType
    margin-left 10px
    margin-right -16px
    padding 0 7px
    .q-icon
      &.on-left
        margin-right 0
        font-size 27px
  #clearOrderType
    margin-left 10px
    margin-right -16px
    padding 0 7px
    .q-icon
      color toolbar-icon-color
      &.on-left
        margin-right 0
        font-size 27px
  .addOrderInput
    width 100%
  #addSession 
      float right
      padding 4px 10px
      margin-left 10px
      margin-right -16px
  .relative
      position relative
      margin-bottom 20px
      .q-icon
          display block
          position absolute
          top 0
          right 17px
          align-items baseline
          vertical-align middle
          width 32px
          height 100%
          text-align center
          padding 6px 7px
          font-size 19px
          cursor pointer
          transition(all 0.2s ease-in-out)
          color toolbar-icon-color
          &:hover
              color $negative
  #class-code
      position absolute
      bottom 0
      left 0
      right 0
      display block
      .q-if-label-above
          font-weight 600
          font-size 20px
</style>
